/**
 * Feb 2026 · Public shoot delivery + sign-off page.
 * Route: /deliver/:token — no auth. Client reviews the shoot summary,
 * draws a signature on a canvas, and either approves or requests changes.
 * On approval the backend auto-drafts an invoice from the booking.
 */
import { useEffect, useRef, useState, useCallback } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { CheckCircle2, ShieldCheck, Camera, PenLine, RotateCcw, Loader2, AlertCircle, FileText, Play, Image as ImageIcon, ExternalLink } from "lucide-react";
import { apiErrorMessage } from "@/lib/apiError";

const API = `${process.env.REACT_APP_BACKEND_URL}/api/v1`;

export default function PublicShootDelivery() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [notes, setNotes] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null); // "approved" | "changes_requested"

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/public/studio/deliver/${token}`);
      setData(data);
      const shoot = data?.shoot || {};
      if (shoot.signoff_status === "approved") setDone("approved");
      else if (shoot.signoff_status === "changes_requested") setDone("changes_requested");
    } catch (e) {
      setError(e?.response?.data?.detail || "Delivery link invalid or expired");
    } finally { setLoading(false); }
  }, [token]);
  useEffect(() => { load(); }, [load]);

  const submit = async (approved) => {
    if (!name.trim()) { toast.error("Please type your full name to sign"); return; }
    const dataUrl = getSignatureDataUrl();
    if (approved && !dataUrl) { toast.error("Please draw your signature before approving"); return; }
    setBusy(true);
    try {
      const { data: res } = await axios.post(`${API}/public/studio/deliver/${token}/signoff`, {
        signed_by_name: name.trim(),
        signed_by_email: email.trim() || undefined,
        approved,
        signature_data_url: dataUrl,
        notes: notes.trim(),
      });
      if (res.already_signed) toast.info("This delivery was already signed off.");
      else toast.success(approved ? "Approved. Thank you!" : "Change request submitted");
      setDone(approved ? "approved" : "changes_requested");
    } catch (e) { toast.error(apiErrorMessage(e, "Submission failed")); }
    finally { setBusy(false); }
  };

  const sigRef = useRef({ getDataUrl: () => null });
  const getSignatureDataUrl = () => sigRef.current?.getDataUrl?.() || null;

  if (loading) return <Center><Loader2 className="w-6 h-6 animate-spin text-primary" /></Center>;
  if (error)   return <Center><ErrorCard message={error} /></Center>;

  const shoot   = data?.shoot   || {};
  const booking = data?.booking || {};
  const ws      = data?.workspace || {};
  const items   = booking.items || [];
  const total   = booking?.quote?.total || 0;

  return (
    <div className="min-h-screen bg-neutral-50" data-testid="public-shoot-delivery">
      {/* Header */}
      <div className="border-b-4 border-primary bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 py-5 flex items-center gap-3">
          <div className="w-10 h-10 border-2 border-primary flex items-center justify-center">
            <Camera className="w-5 h-5 text-primary" />
          </div>
          <div className="flex-1">
            <div className="text-[10px] uppercase tracking-widest text-muted-foreground font-semibold">{ws.name || "Studio"} · Delivery Sign-off</div>
            <h1 className="text-lg sm:text-xl font-bold">Shoot #{booking.code || shoot.id?.slice(0, 8)}</h1>
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 py-6 space-y-6">
        {/* Already-signed banner */}
        {done && (
          <div className={`border-2 p-4 flex items-start gap-3 ${done === "approved" ? "border-emerald-500 bg-emerald-50" : "border-amber-500 bg-amber-50"}`}
               data-testid="signoff-completed-banner">
            <CheckCircle2 className={`w-5 h-5 mt-0.5 ${done === "approved" ? "text-emerald-600" : "text-amber-600"}`} />
            <div className="text-sm">
              <div className="font-semibold">
                {done === "approved" ? "Delivery approved" : "Change request received"}
              </div>
              <div className="text-xs text-muted-foreground mt-1">
                {done === "approved"
                  ? "Thank you. Your invoice will follow shortly by email."
                  : "The studio has been notified of your requested changes."}
              </div>
            </div>
          </div>
        )}

        {/* Summary */}
        <section className="bg-white border border-border p-4 sm:p-5">
          <SectionLabel>Shoot summary</SectionLabel>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mt-3">
            <Field label="Client" value={booking.customer_name || "—"} />
            <Field label="When"  value={booking.slot_start?.replace("T", " ").slice(0, 16) || "—"} />
            <Field label="Phase" value={shoot.phase || "—"} />
            <Field label="Total" value={`₹${Number(total || 0).toFixed(2)}`} mono />
          </div>
        </section>

        {/* Line items */}
        {items.length > 0 && (
          <section className="bg-white border border-border">
            <div className="px-4 sm:px-5 pt-4"><SectionLabel>Included</SectionLabel></div>
            <table className="w-full text-xs mt-2">
              <thead className="bg-neutral-100">
                <tr>
                  {["Item", "Qty", "Rate", "Amount"].map((h) => (
                    <th key={h} className="text-left px-4 sm:px-5 py-1.5 font-semibold uppercase tracking-widest text-[10px] text-muted-foreground">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {items.map((it, i) => (
                  <tr key={i} className="border-t border-border">
                    <td className="px-4 sm:px-5 py-2">{it.name}</td>
                    <td className="px-4 sm:px-5 py-2 font-mono">{it.qty ?? 1}</td>
                    <td className="px-4 sm:px-5 py-2 font-mono">₹{Number(it.rate ?? 0).toFixed(2)}</td>
                    <td className="px-4 sm:px-5 py-2 font-mono">₹{Number(it.amount ?? 0).toFixed(2)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </section>
        )}

        {/* Deliverables */}
        {(shoot.deliverables || []).length > 0 && (
          <section className="bg-white border border-border p-4 sm:p-5">
            <SectionLabel>Deliverables</SectionLabel>
            <div className="mt-3 grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3" data-testid="deliverables-gallery">
              {(shoot.deliverables || []).map((d, i) => (
                <DeliverableTile key={d.id || i} d={d} />
              ))}
            </div>
          </section>
        )}

        {/* Sign-off form */}
        {!done && (
          <section className="bg-white border border-border p-4 sm:p-5 space-y-4" data-testid="signoff-form">
            <div className="flex items-center gap-2">
              <PenLine className="w-4 h-4 text-primary" />
              <SectionLabel>Your sign-off</SectionLabel>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <label className="text-[11px] uppercase tracking-widest font-semibold text-muted-foreground">Full name*</label>
                <input value={name} onChange={(e) => setName(e.target.value)}
                       data-testid="signoff-name"
                       className="w-full h-10 border border-border bg-white px-3 text-sm mt-1 focus:outline-none focus:border-primary" />
              </div>
              <div>
                <label className="text-[11px] uppercase tracking-widest font-semibold text-muted-foreground">Email</label>
                <input value={email} onChange={(e) => setEmail(e.target.value)}
                       data-testid="signoff-email" type="email"
                       className="w-full h-10 border border-border bg-white px-3 text-sm mt-1 focus:outline-none focus:border-primary" />
              </div>
            </div>

            <div>
              <label className="text-[11px] uppercase tracking-widest font-semibold text-muted-foreground">Draw your signature*</label>
              <SignaturePad ref={sigRef} />
            </div>

            <div>
              <label className="text-[11px] uppercase tracking-widest font-semibold text-muted-foreground">Notes (optional)</label>
              <textarea value={notes} onChange={(e) => setNotes(e.target.value)}
                        rows={3} data-testid="signoff-notes"
                        placeholder="Any comments or changes you'd like?"
                        className="w-full border border-border bg-white px-3 py-2 text-sm mt-1 focus:outline-none focus:border-primary" />
            </div>

            <div className="text-[11px] text-muted-foreground flex items-start gap-1.5 border-t border-border pt-3">
              <ShieldCheck className="w-3.5 h-3.5 mt-0.5 text-emerald-600 shrink-0" />
              By signing, you agree the deliverables are as expected and authorize the studio to
              raise a final invoice. Your IP and timestamp are recorded for audit.
            </div>

            <div className="flex flex-col-reverse sm:flex-row sm:justify-end gap-2 pt-2">
              <button onClick={() => submit(false)} disabled={busy}
                      data-testid="signoff-request-changes"
                      className="h-10 px-4 border border-amber-500 text-amber-800 hover:bg-amber-50 text-sm font-semibold uppercase tracking-widest disabled:opacity-50">
                Request changes
              </button>
              <button onClick={() => submit(true)} disabled={busy}
                      data-testid="signoff-approve"
                      className="h-10 px-6 bg-primary text-white hover:opacity-90 text-sm font-semibold uppercase tracking-widest disabled:opacity-50 inline-flex items-center gap-2 justify-center">
                {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />}
                Approve & sign
              </button>
            </div>
          </section>
        )}

        <div className="text-center text-[10px] uppercase tracking-widest text-muted-foreground pt-4 pb-8">
          Powered by {ws.name || "Story and Fame"} · Signed at {new Date().toLocaleDateString()}
        </div>
      </div>
    </div>
  );
}

/* ---------- Signature canvas (forwardRef, minimal, mobile-friendly) ---------- */
import { forwardRef, useImperativeHandle } from "react";

const SignaturePad = forwardRef(function SignaturePad(_, ref) {
  const canvasRef = useRef(null);
  const drawingRef = useRef(false);
  const emptyRef = useRef(true);

  useEffect(() => {
    const c = canvasRef.current; if (!c) return;
    // High-DPI setup.
    const rect = c.getBoundingClientRect();
    const dpr = window.devicePixelRatio || 1;
    c.width = rect.width * dpr; c.height = rect.height * dpr;
    const ctx = c.getContext("2d");
    ctx.scale(dpr, dpr);
    ctx.lineWidth = 2; ctx.lineCap = "round"; ctx.strokeStyle = "#111";
  }, []);

  const pos = (e) => {
    const c = canvasRef.current; const r = c.getBoundingClientRect();
    const t = e.touches?.[0] || e;
    return { x: t.clientX - r.left, y: t.clientY - r.top };
  };
  const start = (e) => {
    e.preventDefault();
    drawingRef.current = true; emptyRef.current = false;
    const p = pos(e); const ctx = canvasRef.current.getContext("2d");
    ctx.beginPath(); ctx.moveTo(p.x, p.y);
  };
  const move = (e) => {
    if (!drawingRef.current) return;
    e.preventDefault();
    const p = pos(e); const ctx = canvasRef.current.getContext("2d");
    ctx.lineTo(p.x, p.y); ctx.stroke();
  };
  const end = () => { drawingRef.current = false; };
  const clear = () => {
    const c = canvasRef.current; const ctx = c.getContext("2d");
    ctx.clearRect(0, 0, c.width, c.height);
    emptyRef.current = true;
  };

  useImperativeHandle(ref, () => ({
    getDataUrl: () => emptyRef.current ? null : canvasRef.current.toDataURL("image/png"),
    clear,
  }));

  return (
    <div className="mt-1">
      <canvas ref={canvasRef}
              data-testid="signoff-canvas"
              onMouseDown={start} onMouseMove={move} onMouseUp={end} onMouseLeave={end}
              onTouchStart={start} onTouchMove={move} onTouchEnd={end}
              className="w-full h-40 border border-border bg-white cursor-crosshair touch-none" />
      <button type="button" onClick={clear} data-testid="signoff-clear"
              className="mt-1 text-[11px] text-muted-foreground hover:text-foreground inline-flex items-center gap-1">
        <RotateCcw className="w-3 h-3" /> Clear signature
      </button>
    </div>
  );
});

/* ---------- Tiny presentational helpers ---------- */
function SectionLabel({ children }) {
  return <div className="text-[10px] uppercase tracking-widest font-semibold text-muted-foreground">{children}</div>;
}
function Field({ label, value, mono }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-widest text-muted-foreground font-semibold">{label}</div>
      <div className={`text-sm mt-0.5 ${mono ? "font-mono" : ""}`}>{value}</div>
    </div>
  );
}
function Center({ children }) {
  return <div className="min-h-screen flex items-center justify-center bg-neutral-50 p-4">{children}</div>;
}
function ErrorCard({ message }) {
  return (
    <div className="max-w-md w-full bg-white border-2 border-rose-500 p-6 text-center">
      <AlertCircle className="w-8 h-8 text-rose-500 mx-auto mb-2" />
      <div className="text-sm font-semibold">Delivery link unavailable</div>
      <div className="text-xs text-muted-foreground mt-1">{message}</div>
    </div>
  );
}

function DeliverableTile({ d }) {
  const url = d.url || "";
  const mime = (d.mime || "").toLowerCase();
  const isImage = mime.startsWith("image/") || /\.(png|jpe?g|webp|gif|svg)(\?|$)/i.test(url);
  const isVideo = mime.startsWith("video/") || /\.(mp4|mov|webm)(\?|$)/i.test(url);
  const isPdf   = mime === "application/pdf" || /\.pdf(\?|$)/i.test(url);
  const thumb = d.thumb_url || (isImage ? url : "");

  return (
    <a href={url || "#"} target={url ? "_blank" : undefined} rel="noopener noreferrer"
       data-testid="deliverable-tile"
       className="group border border-border bg-white hover:border-primary transition-colors flex flex-col overflow-hidden">
      <div className="aspect-video bg-neutral-100 flex items-center justify-center relative overflow-hidden">
        {isImage && thumb ? (
          <img src={thumb} alt={d.name} loading="lazy"
               className="w-full h-full object-cover group-hover:scale-105 transition-transform" />
        ) : isVideo && url ? (
          <>
            <video src={url} preload="metadata" muted playsInline
                    className="w-full h-full object-cover" />
            <span className="absolute inset-0 flex items-center justify-center bg-black/30 group-hover:bg-black/40 transition-colors">
              <Play className="w-8 h-8 text-white drop-shadow" />
            </span>
          </>
        ) : isPdf ? (
          <FileText className="w-10 h-10 text-rose-500" />
        ) : (
          <ImageIcon className="w-10 h-10 text-neutral-400" />
        )}
        {d.status && d.status !== "delivered" && (
          <span className="absolute top-1 left-1 text-[9px] uppercase tracking-widest bg-amber-500 text-white px-1.5 py-0.5">
            {d.status}
          </span>
        )}
      </div>
      <div className="p-2 flex items-start justify-between gap-1 min-w-0">
        <div className="min-w-0">
          <div className="text-xs font-semibold truncate">{d.name || "Deliverable"}</div>
          {d.notes && <div className="text-[10px] text-muted-foreground truncate">{d.notes}</div>}
        </div>
        {url && <ExternalLink className="w-3 h-3 text-muted-foreground shrink-0 mt-0.5" />}
      </div>
    </a>
  );
}
